import {
  type ColumnsType,
  ConfirmModal,
  Loading,
  Table,
  TableRowMenuCell,
  ToggleAllButton,
} from '@/components';
import { useMasterTablePermission } from '@/features/authentication/_hooks/permission';
import { useExpandedRowKeys } from '@/hooks';
import { useMessage } from '@/providers';
import {
  TABLE_CODE_WIDTH,
  TABLE_DATETIME_WIDTH,
  TABLE_ROW_MENU_WIDTH,
  TABLE_YEAR_WIDTH,
  datetimeFormat,
} from '@/utils';
import dayjs from 'dayjs';
import { groupBy, sortBy } from 'lodash-es';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import {
  useAccountTitles,
  useCreateAccountTitle,
  useDeleteAccountTitle,
  useUpdateAccountTitle,
} from '../../hooks';
import {
  type AccountTitleBaseModel,
  AccountingTypeLabelModifier,
} from '../../models';
import {
  DEFAULT_PAGINATION_SETTING,
  fillZeroToAccountTitle,
} from '../../utils';
import { useDrawerState } from '../DrawerStateProvider';
import { AccountTitleFormDrawer } from './AccountTitleFormDrawer';

export const AccountTitlesPage: FC = () => {
  const { t } = useTranslation();
  const { drawerState, setDrawerState } = useDrawerState();
  const { openMessage } = useMessage();
  const accountTitles = useAccountTitles();
  const { mutateAsync: createMutateAsync } = useCreateAccountTitle();
  const { mutateAsync: updateMutateAsync } = useUpdateAccountTitle();
  const { mutateAsync: deleteMutateAsync } = useDeleteAccountTitle();
  const editPermission = useMasterTablePermission(true);
  const {
    expandedRowKeys,
    setExpandedRowKeys,
    onExpand,
    clearExpandedRowKeys,
    addExpandedRowKeys,
  } = useExpandedRowKeys();
  const [accountingTitleDelete, setAccountingTitleDelete] = useState<z.infer<
    typeof AccountTitleBaseModel
  > | null>(null);

  if (accountTitles.isLoading || !accountTitles.data) {
    return <Loading />;
  }

  const selected = accountTitles.data.find(
    (d) =>
      drawerState.open &&
      drawerState.type === 'account-title' &&
      drawerState.isEditing &&
      d.id === drawerState.id,
  );

  const groupedAccountTitles = Object.entries(
    groupBy(accountTitles.data, 'accountingId'),
  ).flatMap(([accountingId, data]) => {
    const accountingType = data.find(
      (d) => d.accountingId === accountingId,
    )?.accountingType;
    if (!accountingType) {
      return [];
    }
    return {
      id: accountingId,
      code: accountingType.code,
      type: accountingType.type,
      name: accountingType.name,
      data: sortBy(data, 'sortOrder'),
    };
  });

  const openAllExpanded = () => {
    setExpandedRowKeys(groupedAccountTitles.map((v) => v.id));
  };

  const handleCloseFormDrawer = async () => {
    setDrawerState({ open: false });
  };

  return (
    <>
      <Table
        enableSticky={false}
        dataSource={groupedAccountTitles}
        rowKey="id"
        title={() => (
          <ToggleAllButton
            isExpandedAllWhenMounted
            openAll={openAllExpanded}
            closeAll={clearExpandedRowKeys}
          />
        )}
        columns={[
          {
            title: t('code'),
            key: 'code',
            dataIndex: 'code',
            width: TABLE_CODE_WIDTH,
          },
          {
            title: t('type'),
            key: 'type',
            dataIndex: 'type',
            width: 120,
            render: (value) => AccountingTypeLabelModifier[value],
          },
          {
            title: t('name'),
            key: 'name',
            dataIndex: 'name',
          },
        ]}
        expandable={{
          expandedRowKeys,
          onExpand: (expanded, record) => {
            onExpand(expanded, record.id);
          },
          rowExpandable: (record) => record.data.length > 0,
          expandedRowRender: (record) => (
            <Table
              enableSticky={false}
              style={{ marginLeft: TABLE_CODE_WIDTH }}
              dataSource={record.data}
              rowKey="id"
              columns={[
                {
                  title: t('code'),
                  key: 'code',
                  dataIndex: 'code',
                  width: TABLE_CODE_WIDTH,
                  render: fillZeroToAccountTitle,
                },
                {
                  title: t('name'),
                  key: 'name',
                  dataIndex: 'name',
                },
                {
                  title: t('start-fiscal-year'),
                  key: 'startYear',
                  dataIndex: 'startYear',
                  width: TABLE_YEAR_WIDTH,
                },
                {
                  title: t('end-fiscal-year'),
                  key: 'endYear',
                  dataIndex: 'endYear',
                  width: TABLE_YEAR_WIDTH,
                },
                {
                  title: t('created-datetime'),
                  key: 'createdAt',
                  dataIndex: 'createdAt',
                  width: TABLE_DATETIME_WIDTH,
                  render: (value) => datetimeFormat({ datetime: value }),
                },
                {
                  title: t('updated-datetime'),
                  key: 'updatedAt',
                  dataIndex: 'updatedAt',
                  width: TABLE_DATETIME_WIDTH,
                  render: (value) => datetimeFormat({ datetime: value }),
                },
                ...(editPermission.data
                  ? ([
                      {
                        title: '',
                        key: 'actions',
                        fixed: 'right' as const,
                        width: TABLE_ROW_MENU_WIDTH,
                        onCell: () => ({
                          style: { minWidth: TABLE_ROW_MENU_WIDTH },
                        }),
                        render: (_, record) => (
                          <TableRowMenuCell
                            onEdit={() => {
                              setDrawerState({
                                open: true,
                                type: 'account-title',
                                isEditing: true,
                                id: record.id,
                              });
                            }}
                            items={[
                              {
                                key: 'delete',
                                label: t('delete'),
                                danger: true,
                                onClick: () => setAccountingTitleDelete(record),
                              },
                            ]}
                          />
                        ),
                      },
                    ] satisfies ColumnsType<(typeof record.data)[number]>)
                  : []),
              ]}
              pagination={false}
            />
          ),
        }}
        pagination={DEFAULT_PAGINATION_SETTING}
      />

      <AccountTitleFormDrawer
        {...(drawerState.open && drawerState.isEditing
          ? {
              type: 'edit',
              initialValues: {
                ...selected,
                period: [
                  selected?.startYear
                    ? dayjs().year(selected.startYear)
                    : undefined,
                  selected?.endYear
                    ? dayjs().year(selected.endYear)
                    : undefined,
                ],
              },
            }
          : { type: 'new' })}
        onSubmit={async (values) => {
          invariant(drawerState.open, t('unreachable-code'));

          const res = drawerState.isEditing
            ? await updateMutateAsync({ ...values, id: drawerState.id })
            : await createMutateAsync(values);

          await Promise.all([
            openMessage(
              'success',
              drawerState.isEditing
                ? t('message-update-completed')
                : t('message-create-completed'),
            ),
            handleCloseFormDrawer(),
            !drawerState.isEditing &&
              (async () => {
                addExpandedRowKeys([res.accountingId]);
              })(),
          ]);
        }}
      />
      <ConfirmModal
        open={accountingTitleDelete !== null}
        title={t('delete-confirmation')}
        disclaimerText={t('delete-confirmation-disclaimer')}
        onOk={async () => {
          invariant(accountingTitleDelete, t('unreachable-code'));
          try {
            await deleteMutateAsync(accountingTitleDelete.id);
            await Promise.all([
              openMessage('success', t('message-delete-completed')),
              (async () => {
                setAccountingTitleDelete(null);
              })(),
            ]);
          } catch {
            await openMessage('error', t('message-delete-failed'));
          }
        }}
        onCancel={() => setAccountingTitleDelete(null)}
        skipCheck
      />
    </>
  );
};
